/**
 * page-backoffice is Sluice Cloud's own operator console, routed at
 * `/backoffice`.
 *
 * Features: `SC-F903` (the console), `SC-F305` (orphan quarantine and the
 * blast-radius guard), `SC-F304` (reconcile state), `SC-F904` (capacity and node
 * inventory, including the state-device alarm that is a DATA-PLANE alarm rather
 * than a capacity one), `SC-F905` (the support diagnostics bundle, defined by what
 * it excludes) and `SC-F915` (the staff principal every route here depends on).
 * Stories: `SC-S902`, `SC-S306`, `SC-S307`, `SC-S903`, `SC-S908`, `SC-S300`.
 *
 * **This is our console, not a customer surface, and it is drawn so that nobody
 * can mistake it for one.** Every read here crosses tenancy boundaries, so the
 * page wears an operations band and a warning-toned frame that appears nowhere
 * else in the product. The boundary that keeps it defensible is stated on the
 * page itself and encoded in what the API returns: staff see **state and
 * shape** — organizations, instances, generations, reconcile outcomes — and
 * never a secret value, never policy content, never a customer's query results.
 *
 * The reconciler's guards get top billing because they are load-bearing.
 * `SC-F305`'s four rules exist to prevent a fleet outage, and `SC-S903` makes a
 * control-plane restore depend on them holding: a backup that predates several
 * instance creations looks, to reconciliation, exactly like an instruction to
 * delete every instance created since. An operator has to be able to see the
 * guard holding, read the plan it refused, and override it deliberately with a
 * recorded reason — which is the one flow this page treats as its centrepiece.
 *
 * Tabs live in the URL fragment so an operator can send a colleague the panel
 * they are looking at; `router.ts` owns one path for this console.
 */

import { LitElement, css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import {
  toast,
  toastPersistent,
  type BnBadgeVariant,
  type BnPageChangeDetail,
  type BnColumn,
  type BnKvRow,
  type BnRow,
  type BnRowClickDetail,
  type BnTable,
  type BnSelectChangeDetail,
  type BnSelectOption,
  type BnTab,
  type BnTabsChangeDetail,
} from "../components/index.js";

import { verifyWithPasskey } from "../api/auth.js";
import {
  CHANGE_CLASS_LABELS,
  GENERATION_STATE_LABELS,
  restartClass,
} from "../api/generations.js";
import {
  acknowledgeBlastRadius,
  acknowledgeQuarantineRemoval,
  getCapacity,
  getDiagnostics,
  getFleetInstance,
  getFleetSummary,
  getReconcileStatus,
  grantOwner,
  listBackofficeOrgMembers,
  listBackofficeOrgs,
  listFleetInstances,
  listQuarantined,
  releaseQuarantine,
  setReconcileMode,
  type BackofficeMember,
  type BackofficeOrg,
  type CapacityView,
  type DiagnosticsBundle,
  type DiskAlarm,
  type FleetInstance,
  type FleetInstanceDetail,
  type FleetSummary,
  type PoolRow,
  type QuarantinedObject,
  type ReconcileStatus,
} from "../api/backoffice.js";
import { ApiError, parseError } from "../api/errors.js";
import type { InstanceState } from "../api/instances.js";
import { hrefFor } from "../nav.js";

/** How often the reconciler panel re-reads itself. The guard is the thing an
 * operator watches during an incident, so it must not need a manual refresh. */
const RECONCILE_POLL_MS = 10000;

const TABS: BnTab[] = [
  { id: "fleet", label: "Fleet and reconciler" },
  { id: "orgs", label: "Organizations" },
  { id: "instances", label: "Instances" },
  { id: "quarantine", label: "Quarantine" },
  // `SC-F904`. Last because it is the only tab that is about our hardware rather
  // than about a customer's tenancy.
  { id: "capacity", label: "Capacity" },
];

/** The state device's four alarm levels, tone-mapped (`SC-F904`).
 *
 * `critical` is `error` and not `warning`, and that is the whole point of the type:
 * a full state device is not a housekeeping task, it is a fail-closed data-plane
 * outage for every instance pinned to the node. `uninventoried` is `neutral` because
 * "we did not measure" must never render as either "there is room" or "it is full". */
const ALARM_TONE: Record<DiskAlarm, BnBadgeVariant> = {
  none: "success",
  uninventoried: "neutral",
  tight: "warning",
  critical: "error",
};

const ALARM_LABEL: Record<DiskAlarm, string> = {
  none: "audit volume headroom ok",
  uninventoried: "device not inventoried",
  tight: "audit volume tight",
  critical: "audit volume critical",
};

const NODE_COLUMNS: BnColumn[] = [
  { key: "node", label: "Node", mono: true },
  { key: "class", label: "Class", mono: true, width: "1px" },
  { key: "memory", label: "Memory free", align: "right", width: "1px", mono: true },
  { key: "cpu", label: "CPU free", align: "right", width: "1px", mono: true },
  { key: "state", label: "Audit volume free", align: "right", width: "1px", mono: true },
  { key: "alarm", label: "Data-plane alarm", width: "1px" },
  { key: "fits", label: "Still fits", mono: true },
];

/** `02` §2.1's eleven states, tone-mapped. Kept flat rather than shared with the
 * customer console: this page is a different module and importing a page from a
 * page would couple two surfaces that should be able to move separately. */
const STATE_TONE: Record<InstanceState, BnBadgeVariant> = {
  draft: "neutral",
  provisioning: "info",
  starting: "info",
  healthy: "success",
  degraded: "warning",
  updating: "info",
  "rolling-back": "warning",
  suspended: "neutral",
  deleting: "warning",
  deleted: "neutral",
  failed: "error",
};

const ORG_COLUMNS: BnColumn[] = [
  { key: "slug", label: "Organization", mono: true },
  { key: "name", label: "Name" },
  { key: "kind", label: "Kind", width: "1px" },
  { key: "owners", label: "Owners", align: "right", width: "1px", mono: true },
  { key: "members", label: "Members", align: "right", width: "1px", mono: true },
  { key: "instances", label: "Instances", align: "right", width: "1px", mono: true },
  { key: "actions", label: "", width: "1px" },
];

const INSTANCE_COLUMNS: BnColumn[] = [
  { key: "name", label: "Instance", mono: true },
  { key: "org", label: "Organization", mono: true },
  { key: "hostname", label: "Hostname", mono: true },
  { key: "state", label: "State", width: "1px" },
  { key: "generations", label: "Gen (live/observed)", align: "right", width: "1px", mono: true },
  { key: "node", label: "Node", mono: true },
  { key: "service", label: "Service id", mono: true },
];

const QUARANTINE_COLUMNS: BnColumn[] = [
  { key: "name", label: "Object", mono: true },
  { key: "kind", label: "Kind", width: "1px" },
  { key: "org", label: "Org label", mono: true },
  { key: "instance", label: "Instance label", mono: true },
  { key: "stage", label: "Stage", width: "1px" },
  { key: "seen", label: "Observed", align: "right", width: "1px", mono: true },
  { key: "since", label: "First seen", width: "1px" },
  { key: "actions", label: "", width: "1px" },
];

const PLAN_COLUMNS: BnColumn[] = [
  { key: "action", label: "Action", width: "1px" },
  { key: "kind", label: "Object", width: "1px" },
  { key: "name", label: "Name", mono: true },
  { key: "org", label: "Org label", mono: true },
  { key: "instance", label: "Instance label", mono: true },
  { key: "reason", label: "Reason" },
];

const GENERATION_COLUMNS: BnColumn[] = [
  { key: "number", label: "#", align: "right", width: "1px", mono: true },
  { key: "state", label: "State", width: "1px" },
  { key: "class", label: "Class", width: "1px" },
  { key: "digest", label: "Bundle digest", mono: true },
  { key: "by", label: "Published by" },
  { key: "when", label: "When", width: "1px" },
];

/** Relative time. Local to the page for the same reason it is local to
 * page-instance: the kit ships no date helper and adding a shared one is not
 * this task's to make. */
function ago(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const s = Math.round((Date.now() - then) / 1000);
  if (s < 45) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? "" : "s"} ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} hour${h === 1 ? "" : "s"} ago`;
  return `${Math.round(h / 24)} days ago`;
}

function shortDigest(d: string | null | undefined): string {
  if (!d) return "—";
  const body = d.startsWith("sha256:") ? d.slice(7) : d;
  return body.length > 16 ? `${body.slice(0, 12)}…${body.slice(-4)}` : body;
}

/** Which confirmation dialog is open. One field rather than five booleans, so
 * two dialogs can never be open at once. */
type Dialog =
  | { kind: "override" }
  | { kind: "mode"; to: "enforce" | "observe" }
  | { kind: "grant"; org: BackofficeOrg }
  | { kind: "quarantine-ack"; object: QuarantinedObject }
  | { kind: "quarantine-release"; object: QuarantinedObject }
  | null;

/**
 * page-backoffice renders the staff console. It takes no properties: it is a
 * fleet-wide surface and its scope is the whole control plane.
 *
 *   <page-backoffice></page-backoffice>
 */
@customElement("page-backoffice")
export class PageBackoffice extends LitElement {
  @state() private tab = "fleet";

  /* Fleet and reconciler. */
  @state() private summary: FleetSummary | null = null;
  @state() private summaryError: ApiError | null = null;
  @state() private reconcile: ReconcileStatus | null = null;
  @state() private reconcileError: ApiError | null = null;
  @state() private reconcileLoading = true;

  /* Capacity and node inventory (`SC-F904`). */
  @state() private capacity: CapacityView | null = null;
  @state() private capacityError: ApiError | null = null;
  @state() private capacityLoading = false;

  /* The support diagnostics bundle (`SC-F905`). */
  @state() private diagnostics: DiagnosticsBundle | null = null;
  @state() private diagnosticsError: ApiError | null = null;
  @state() private diagnosticsBuilding = false;
  @state() private diagnosticsTicket = "";

  /* Organizations. */
  @state() private orgs: BackofficeOrg[] = [];
  @state() private orgsNext: string | null = null;
  @state() private orgsLoading = false;
  @state() private orgsError: ApiError | null = null;
  @state() private orgQuery = "";

  /* Instances. */
  @state() private instances: FleetInstance[] = [];
  @state() private instancesNext: string | null = null;
  @state() private instancesLoading = false;
  @state() private instancesError: ApiError | null = null;
  @state() private instanceQuery = "";
  @state() private openInstance: FleetInstanceDetail | null = null;
  @state() private openInstanceError: ApiError | null = null;
  @state() private openInstanceLoading = false;
  @state() private drawerOpen = false;

  /* Quarantine. */
  @state() private orphans: QuarantinedObject[] = [];
  @state() private orphansNext: string | null = null;
  @state() private orphansLoading = false;
  @state() private orphansError: ApiError | null = null;
  @state() private includeCandidates = true;

  /* Dialogs and the one mutating flow each of them owns. */
  @state() private dialog: Dialog = null;
  @state() private reason = "";
  @state() private ticket = "";
  @state() private grantAccount = "";
  @state() private grantMembers: BackofficeMember[] = [];
  @state() private grantMembersError: ApiError | null = null;
  @state() private submitting = false;
  @state() private dialogError: ApiError | null = null;

  private reconcileTimer = 0;
  private readonly onHashChange = () => this.syncTabFromHash();

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }

    /* The console frame. Nothing else in the product is drawn like this, which
       is the point: a cross-tenant surface must be unmistakable at a glance,
       including in a screenshot pasted into a ticket. */
    .console {
      border: 2px solid var(--bino-warning-border);
      border-radius: var(--bino-radius-lg);
      background: var(--bino-surface);
      overflow: hidden;
    }
    .band {
      /* Hazard stripes, built entirely from theme tokens so they follow light
         and dark like everything else. */
      background: repeating-linear-gradient(
        135deg,
        var(--bino-warning-bg) 0 12px,
        var(--bino-surface-subtle) 12px 24px
      );
      border-bottom: 2px solid var(--bino-warning-border);
      padding: var(--bino-space-xs) var(--bino-space-lg);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      font-weight: 600;
      letter-spacing: 0.14em;
      text-transform: uppercase;
      color: var(--bino-warning-text);
    }
    .frame {
      padding: var(--bino-space-lg);
    }

    header.page {
      display: flex;
      flex-wrap: wrap;
      align-items: flex-start;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
      flex: 1 1 18rem;
    }
    .counters {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
    }
    .counter {
      min-width: 6rem;
    }
    .counter .n {
      display: block;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      line-height: 1.1;
    }
    .counter .k {
      display: block;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }

    p {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      line-height: 1.5;
      max-width: 80ch;
    }
    p:last-child {
      margin-bottom: 0;
    }
    .muted {
      color: var(--bino-text-secondary);
      font-size: var(--bino-font-size-base);
    }
    /* SC-F904's caveat and isolation lists, and SC-F905's manifest. A plain list
       rather than prose because every item is a separate claim a reader has to be
       able to quote on its own. */
    .bullets {
      margin: 0;
      padding-left: var(--bino-space-lg);
      display: grid;
      gap: var(--bino-space-xs);
      max-width: 80ch;
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
    }
    .bullets strong {
      color: var(--bino-text);
    }
    .sub {
      display: block;
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-muted);
    }
    h2 {
      margin: 0 0 var(--bino-space-sm);
      font-size: var(--bino-font-size-md);
      font-weight: 600;
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      overflow-wrap: anywhere;
    }
    a.inline {
      color: var(--bino-primary);
    }

    .stack > * + * {
      margin-top: var(--bino-space-md);
    }
    .row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-sm);
    }
    .toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      align-items: flex-end;
      margin-bottom: var(--bino-space-md);
    }
    .search {
      flex: 1 1 20rem;
      min-width: 0;
    }

    /* The four rules of the blast-radius feature, each with the number that
       shows it working. A guard nobody can see is a guard nobody trusts. */
    .rules {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
      gap: var(--bino-space-md);
      margin-top: var(--bino-space-md);
    }
    .rule {
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      padding: var(--bino-space-md);
      background: var(--bino-surface-subtle);
    }
    .rule .n {
      display: block;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
    }
    .rule h3 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-text-secondary);
    }
    .rule p {
      font-size: var(--bino-font-size-base);
      color: var(--bino-text-secondary);
      margin: var(--bino-space-xs) 0 0;
    }
  `;

  /* ── lifecycle ─────────────────────────────────────────────────────────── */

  override connectedCallback() {
    super.connectedCallback();
    this.syncTabFromHash();
    window.addEventListener("hashchange", this.onHashChange);
    void this.loadFleet();
    this.reconcileTimer = window.setInterval(() => void this.loadReconcile(), RECONCILE_POLL_MS);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("hashchange", this.onHashChange);
    if (this.reconcileTimer) window.clearInterval(this.reconcileTimer);
    this.reconcileTimer = 0;
  }

  private syncTabFromHash() {
    const id = location.hash.replace(/^#/, "");
    this.selectTab(TABS.some((t) => t.id === id) ? id : "fleet", false);
  }

  private selectTab(id: string, writeHash = true) {
    this.tab = id;
    if (writeHash) history.replaceState({}, "", `${location.pathname}${location.search}#${id}`);
    if (id === "orgs" && this.orgs.length === 0 && !this.orgsError) void this.loadOrgs();
    if (id === "instances" && this.instances.length === 0 && !this.instancesError)
      void this.loadInstances();
    if (id === "quarantine" && this.orphans.length === 0 && !this.orphansError)
      void this.loadOrphans();
    if (id === "capacity" && this.capacity === null && !this.capacityError) void this.loadCapacity();
  }

  /** Reads the capacity ledger (`SC-F904`). It is loaded on first visit rather than
   * with the fleet, because the read crosses the internal API to the orchestration
   * component and the fleet header must not wait on it. */
  private async loadCapacity() {
    this.capacityLoading = true;
    try {
      this.capacity = await getCapacity();
      this.capacityError = null;
    } catch (err) {
      this.capacityError = parseError(err);
      this.capacity = null;
    } finally {
      this.capacityLoading = false;
    }
  }

  /** Collects the support diagnostics bundle for the open instance (`SC-F905`).
   *
   * It is a READ that stores nothing, so there is no confirmation dialog and no
   * step-up ceremony — but every build is recorded in the customer's own activity
   * feed, uncoalesced, which is why the button says so before it is pressed. */
  private async buildDiagnostics(instanceId: string) {
    this.diagnosticsBuilding = true;
    try {
      this.diagnostics = await getDiagnostics(instanceId, {
        ticket: this.diagnosticsTicket.trim() || undefined,
      });
      this.diagnosticsError = null;
      toast("Diagnostics collected. The organization's activity feed records it.", "success");
    } catch (err) {
      this.diagnosticsError = parseError(err);
      this.diagnostics = null;
    } finally {
      this.diagnosticsBuilding = false;
    }
  }

  /** Downloads the collected bundle as a JSON file.
   *
   * The artifact is assembled per request and stored nowhere, so the download is the
   * only copy that exists — which is the property that keeps a customer's operational
   * state out of our database waiting to be a breach. */
  private downloadDiagnostics() {
    const bundle = this.diagnostics;
    if (!bundle) return;
    const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `diagnostics-${bundle.instance.name}-${bundle.collected_at}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /* ── loading ───────────────────────────────────────────────────────────── */

  private async loadFleet() {
    await Promise.all([this.loadSummary(), this.loadReconcile()]);
  }

  private async loadSummary() {
    try {
      this.summary = await getFleetSummary();
      this.summaryError = null;
    } catch (err) {
      this.summaryError = parseError(err);
    }
  }

  private async loadReconcile() {
    try {
      this.reconcile = await getReconcileStatus();
      this.reconcileError = null;
    } catch (err) {
      this.reconcileError = parseError(err);
    } finally {
      this.reconcileLoading = false;
    }
  }

  /**
   * A keyset cursor is only valid for the query that produced it, so a new
   * search must drop the pager's cursor stack rather than paging a filter that
   * no longer applies. bn-table owns the stack; this is how it is told.
   */
  private resetPager(id: string) {
    this.renderRoot.querySelector<BnTable>(`#${id}`)?.resetPagination();
  }

  private async loadOrgs(cursor?: string) {
    this.orgsLoading = true;
    try {
      const page = await listBackofficeOrgs({ q: this.orgQuery || undefined, cursor });
      this.orgs = page.items;
      this.orgsNext = page.next;
      this.orgsError = null;
    } catch (err) {
      this.orgsError = parseError(err);
    } finally {
      this.orgsLoading = false;
    }
  }

  private async loadInstances(cursor?: string) {
    this.instancesLoading = true;
    try {
      const page = await listFleetInstances({ q: this.instanceQuery || undefined, cursor });
      this.instances = page.items;
      this.instancesNext = page.next;
      this.instancesError = null;
    } catch (err) {
      this.instancesError = parseError(err);
    } finally {
      this.instancesLoading = false;
    }
  }

  private async loadOrphans(cursor?: string) {
    this.orphansLoading = true;
    try {
      const page = await listQuarantined({ include_candidates: this.includeCandidates, cursor });
      this.orphans = page.items;
      this.orphansNext = page.next;
      this.orphansError = null;
    } catch (err) {
      this.orphansError = parseError(err);
    } finally {
      this.orphansLoading = false;
    }
  }

  private async openInstanceDrawer(id: string) {
    this.drawerOpen = true;
    this.openInstance = null;
    this.openInstanceError = null;
    this.openInstanceLoading = true;
    try {
      this.openInstance = await getFleetInstance(id);
    } catch (err) {
      this.openInstanceError = parseError(err);
    } finally {
      this.openInstanceLoading = false;
    }
  }

  /* ── the mutating flows ────────────────────────────────────────────────── */

  private openDialog(dialog: Dialog) {
    this.dialog = dialog;
    this.reason = "";
    this.ticket = "";
    this.grantAccount = "";
    this.dialogError = null;
    this.grantMembers = [];
    this.grantMembersError = null;
    if (dialog?.kind === "grant") void this.loadGrantMembers(dialog.org.id);
  }

  private async loadGrantMembers(orgId: string) {
    try {
      const page = await listBackofficeOrgMembers(orgId);
      this.grantMembers = page.items;
      this.grantMembersError = null;
    } catch (err) {
      this.grantMembersError = parseError(err);
    }
  }

  /**
   * Runs a staff mutation, obtaining a step-up grant on demand.
   *
   * `SC-S908` requires a step-up grant on every mutating staff action, and the
   * grant carries the **target**, so one taken for an organization cannot be
   * spent on another. Rather than pre-flighting a ceremony nobody may need,
   * this attempts the call, and on `verification_required` runs the passkey
   * assertion for that exact target and retries once. Staff sign-in is
   * passkey-only with no code fallback, so the ceremony is always available.
   */
  private async submit(
    run: (verification?: string) => Promise<unknown>,
    stepUp: { action: "change_owner"; target: string } | null,
    success: string,
  ) {
    this.submitting = true;
    this.dialogError = null;
    try {
      try {
        await run();
      } catch (err) {
        const e = parseError(err);
        if (e.code !== "verification_required" || !stepUp) throw err;
        const grant = await verifyWithPasskey(stepUp);
        await run(grant);
      }
      toast(success, "success");
      this.dialog = null;
      await Promise.all([this.loadReconcile(), this.loadSummary()]);
      if (this.tab === "quarantine") await this.loadOrphans();
      if (this.tab === "orgs") await this.loadOrgs();
    } catch (err) {
      const e = parseError(err);
      this.dialogError = e;
      toastPersistent(e.message, "error", "The action was refused");
    } finally {
      this.submitting = false;
    }
  }

  /* ── shared render helpers ─────────────────────────────────────────────── */

  /** The failure state every panel here uses: the `apierr` code, its message and
   * the request id a reader quotes in a ticket. */
  private renderError(heading: string, err: ApiError, retry?: () => void): TemplateResult {
    return html`
      <bn-card heading=${heading}>
        ${retry
          ? html`<bn-button slot="actions" size="sm" @click=${retry}>Try again</bn-button>`
          : nothing}
        <div role="alert">
          <p>${err.message}</p>
          <bn-kv
            .rows=${[
              { label: "Error code", value: err.code },
              { label: "Request id", value: err.requestId || undefined },
            ] as BnKvRow[]}
            unsetText="never reached the server"
          ></bn-kv>
          ${err.code === "forbidden"
            ? html`<p class="muted">
                Every route under the backoffice requires a staff principal, and it refuses every
                customer credential class unconditionally — a session, a general key, a
                fine-grained token and an instance credential all receive this, whatever
                organization roles they carry.
              </p>`
            : html`<p class="muted">
                ${err.requestId
                  ? "Quote the request id when escalating."
                  : "This request did not reach the control plane, so there is no reference to quote."}
              </p>`}
        </div>
      </bn-card>
    `;
  }

  /* ── fleet and reconciler ──────────────────────────────────────────────── */

  private renderFleet(): TemplateResult {
    return html`
      <div class="stack">
        ${this.summaryError
          ? this.renderError("Fleet counters unavailable", this.summaryError, () =>
              void this.loadSummary(),
            )
          : this.renderSummary()}
        ${this.reconcileError
          ? this.renderError("Reconciler state unavailable", this.reconcileError, () =>
              void this.loadReconcile(),
            )
          : this.reconcileLoading && !this.reconcile
            ? html`<bn-card heading="Reconciler"
                ><bn-spinner block label="Reading reconciler state"></bn-spinner
              ></bn-card>`
            : this.reconcile
              ? html`${this.renderGuard(this.reconcile)} ${this.renderRules(this.reconcile)}
                ${this.renderHatches(this.reconcile)}`
              : nothing}
      </div>
    `;
  }

  private renderSummary(): TemplateResult {
    const s = this.summary;
    if (!s) {
      return html`<bn-card heading="Fleet"
        ><bn-spinner block label="Reading fleet counters"></bn-spinner
      ></bn-card>`;
    }
    const attention = (["failed", "degraded", "deleting"] as InstanceState[]).reduce(
      (n, k) => n + (s.instances_by_state[k] ?? 0),
      0,
    );
    return html`
      <bn-card heading="Fleet">
        <div class="counters">
          ${counter(s.organizations, "organizations")} ${counter(s.instances, "instances")}
          ${counter(s.managed_services, "managed services")}
          ${counter(attention, "needing attention")}
          ${counter(s.quarantined_objects, "quarantined objects")}
          ${counter(s.jobs_in_flight, "jobs in flight")}
        </div>
        <div class="row" style="margin-top:var(--bino-space-md)">
          ${(Object.keys(s.instances_by_state) as InstanceState[]).map(
            (k) =>
              html`<bn-badge variant=${STATE_TONE[k] ?? "neutral"}
                >${k} · ${s.instances_by_state[k]}</bn-badge
              >`,
          )}
        </div>
      </bn-card>
    `;
  }

  /** `SC-F305` rule 3 and `SC-S307`: when the guard has refused a pass, the
   * intended plan is visible in full and the override names a reason. */
  private renderGuard(r: ReconcileStatus): TemplateResult {
    const g = r.guard;
    const threshold = Math.max(
      g.threshold_floor,
      Math.ceil(g.threshold_fraction * g.managed_services),
    );

    if (!g.holding) {
      return html`
        <bn-card heading="Blast-radius guard">
          <div class="row">
            <bn-badge variant="success" dot>Holding — nothing refused</bn-badge>
            <span class="muted"
              >last pass ${r.last_pass ? ago(r.last_pass) : "not yet run"}${r.last_outcome
                ? ` · ${r.last_outcome}`
                : ""}</span
            >
          </div>
          <p style="margin-top:var(--bino-space-md)">
            A single pass may quarantine or delete at most
            <strong>${threshold}</strong> managed objects — the greater of ${g.threshold_floor} and
            ${Math.round(g.threshold_fraction * 100)}% of the ${g.managed_services} services we
            manage. Past that the whole pass is refused, nothing changes, and it stays refused until
            an operator overrides it. This one rule prevents the worst outage available to us: a
            label-filter bug or a restore from an older snapshot mass-deleting the fleet.
          </p>
        </bn-card>
      `;
    }

    const rows: BnRow[] = g.plan.map((p) => ({
      action: html`<bn-badge variant=${p.action === "quarantine" ? "warning" : "error"}
        >${p.action.replace("_", " ")}</bn-badge
      >`,
      kind: p.object_kind,
      name: p.swarm_name,
      org: p.org_label || "—",
      instance: p.instance_label || "—",
      reason: p.reason,
    }));

    return html`
      <bn-card heading="Blast-radius guard — a pass was refused">
        <bn-button
          slot="actions"
          variant="danger"
          size="sm"
          @click=${() => this.openDialog({ kind: "override" })}
          >Override once</bn-button
        >
        <div class="row">
          <bn-badge variant="error" dot>Refused ${ago(g.refused_at)}</bn-badge>
          <bn-badge variant="warning">${g.planned_action_count} actions planned</bn-badge>
          <bn-badge variant="neutral">threshold ${threshold}</bn-badge>
        </div>
        <p style="margin-top:var(--bino-space-md)">
          The pass wanted to take ${g.planned_action_count} destructive actions against
          ${g.managed_services} managed services, which is over the threshold, so
          <strong>nothing was changed</strong>. Every subsequent pass also refuses — the guard does
          not reset itself on the next tick — until the override below is given. Read the plan
          first: if these are instances that exist and the database does not know about them, the
          control plane is what is wrong, not the cluster.
        </p>
        <div style="margin-top:var(--bino-space-md)">
          <bn-table
            .columns=${PLAN_COLUMNS}
            .rows=${rows}
            caption="The plan the guard refused"
            empty="The guard is holding but reported no plan entries."
          ></bn-table>
        </div>
      </bn-card>
    `;
  }

  /** The four rules, each with the counter that shows it working. */
  private renderRules(r: ReconcileStatus): TemplateResult {
    const threshold = Math.max(
      r.guard.threshold_floor,
      Math.ceil(r.guard.threshold_fraction * r.guard.managed_services),
    );
    return html`
      <bn-card heading="Orphan handling — the four rules">
        <p class="muted">
          An orphan is an anchor Service or a job Pod carrying our labels with no database row. It is usually a
          create whose commit is still in flight; it is sometimes a control-plane bug that would
          otherwise delete a paying customer's running instance. Quarantine is not an instance
          state — by definition there is no instance record to put in one.
        </p>
        <div class="rules">
          <div class="rule">
            <h3>1 · Never act on first sight</h3>
            <span class="n">${r.candidate_orphans}</span>
            <p>
              candidate orphans observed and left alone. An orphan must be seen on
              ${r.orphan_observation_threshold} consecutive passes before anything happens to it.
            </p>
          </div>
          <div class="rule">
            <h3>2 · Quarantine before delete</h3>
            <span class="n">${r.quarantined_objects}</span>
            <p>
              objects quarantined, none removed. An instance's Pod is deleted and its anchor Service
              stamped; the Service, its Secrets and the data directory stay. A Pod can be started
              again; a removed data directory cannot, and the audit chain cannot be rebuilt.
            </p>
          </div>
          <div class="rule">
            <h3>3 · Blast-radius guard</h3>
            <span class="n">${threshold}</span>
            <p>
              destructive actions is the per-pass ceiling. Over it, the whole pass is refused and
              stays refused. ${r.guard.holding ? "It is refusing right now." : "It is not tripped."}
            </p>
          </div>
          <div class="rule">
            <h3>4 · Delayed, refcounted sweep</h3>
            <span class="n">${r.pending_object_sweeps}</span>
            <p>
              objects waiting out the ${Math.round(r.object_sweep_delay_seconds / 60)}-minute
              delay. This sweep is not built on k3s: a superseded Secret goes with its anchor
              Service and files go with reclaim, so the count stays at zero.
            </p>
          </div>
        </div>
      </bn-card>
    `;
  }

  /** `02` §9.7's three manual escape hatches, all recorded with actor, reason
   * and duration, and all surfaced so nobody debugs a paused reconciler. */
  private renderHatches(r: ReconcileStatus): TemplateResult {
    return html`
      <bn-card heading="Reconciler">
        <bn-kv
          .rows=${[
            { label: "Mode", value: r.mode },
            {
              label: "Last pass",
              value: r.last_pass ? `${r.last_pass} (${ago(r.last_pass)})` : undefined,
            },
            { label: "Last outcome", value: r.last_outcome ?? undefined },
            { label: "Lease holder", value: r.lease_holder ?? undefined },
            { label: "Instances paused", value: String(r.paused_instances) },
          ] as BnKvRow[]}
          unsetText="not recorded"
        ></bn-kv>
        <div class="row" style="margin-top:var(--bino-space-md)">
          ${r.mode === "observe"
            ? html`<bn-badge variant="warning" dot>Observe-only — no mutations fleet-wide</bn-badge>
                <bn-button
                  size="sm"
                  variant="primary"
                  @click=${() => this.openDialog({ kind: "mode", to: "enforce" })}
                  >Resume enforcing</bn-button
                >`
            : html`<bn-badge variant="success" dot>Enforcing</bn-badge>
                <bn-button
                  size="sm"
                  variant="danger"
                  @click=${() => this.openDialog({ kind: "mode", to: "observe" })}
                  >Switch to observe-only</bn-button
                >`}
        </div>
        <p class="muted" style="margin-top:var(--bino-space-md)">
          Observe-only is the switch to throw when the reconciler itself is suspected of
          misbehaving: it keeps observing and reporting and performs no mutation anywhere. It is
          recorded with your identity, your reason and its duration, and it shows on every affected
          instance so nobody spends an hour debugging a reconciler that was deliberately paused.
        </p>
      </bn-card>
    `;
  }

  /* ── organizations ─────────────────────────────────────────────────────── */

  private renderOrgs(): TemplateResult {
    const rows: BnRow[] = this.orgs.map((o) => ({
      id: o.id,
      slug: o.slug,
      name: o.name,
      kind: o.personal
        ? html`<bn-badge variant="neutral">personal</bn-badge>`
        : html`<bn-badge variant="info">organization</bn-badge>`,
      owners:
        o.owner_count === 0
          ? html`<bn-badge variant="error">0</bn-badge>`
          : String(o.owner_count),
      members: String(o.member_count),
      instances: String(o.instance_count),
      actions: html`<bn-button size="sm" @click=${() => this.openDialog({ kind: "grant", org: o })}
        >Grant owner</bn-button
      >`,
    }));

    return html`
      <div class="stack">
        <bn-panel eyebrow="What staff may do here">
          <p>
            Read organizations, their instances and their reconcile state. The console carries
            exactly <strong>one</strong> membership-mutating action — granting the owner role to an
            existing member — and it exists because the last-owner invariant otherwise strands an
            organization whose only owner has become unreachable. It grants. It never removes and
            never demotes: staff cannot take an organization away from its owners.
          </p>
        </bn-panel>

        <bn-card heading="Organizations" flush>
          <div slot="actions" class="row">
            <bn-button size="sm" @click=${() => void this.loadOrgs()}>Refresh</bn-button>
          </div>
          <div style="padding:var(--bino-space-lg) var(--bino-space-lg) 0">
            <div class="toolbar">
              <div class="search">
                <bn-field label="Search organizations" hint="Matches the slug and the display name.">
                  <bn-input
                    .value=${this.orgQuery}
                    placeholder="acme"
                    @input=${(e: CustomEvent<{ value: string }>) => (this.orgQuery = e.detail.value)}
                  ></bn-input>
                </bn-field>
              </div>
              <bn-button
                variant="primary"
                @click=${() => {
                  this.resetPager("orgs-table");
                  void this.loadOrgs();
                }}
                >Search</bn-button
              >
            </div>
          </div>
          ${this.orgsError
            ? html`<div style="padding:0 var(--bino-space-lg) var(--bino-space-lg)">
                ${this.renderError("Organizations unavailable", this.orgsError, () =>
                  void this.loadOrgs(),
                )}
              </div>`
            : html`<bn-table
                id="orgs-table"
                .columns=${ORG_COLUMNS}
                .rows=${rows}
                ?loading=${this.orgsLoading}
                paginated
                .nextCursor=${this.orgsNext}
                caption="Organizations across the fleet"
                @page-change=${(e: CustomEvent<BnPageChangeDetail>) =>
                  void this.loadOrgs(e.detail.cursor ?? undefined)}
              >
                <bn-empty-state
                  slot="empty"
                  icon="◫"
                  heading=${this.orgQuery ? "No organization matches" : "No organizations"}
                  message=${this.orgQuery
                    ? "Nothing matched that search. The filter matches the slug and the display name, not member emails."
                    : "The control plane has no organizations yet. One is created for every account on first sign-in."}
                ></bn-empty-state>
              </bn-table>`}
        </bn-card>
      </div>
    `;
  }

  /* ── instances ─────────────────────────────────────────────────────────── */

  private renderInstances(): TemplateResult {
    const rows: BnRow[] = this.instances.map((i) => ({
      id: i.id,
      name: i.name,
      org: i.org_slug,
      hostname: i.hostname,
      state: html`<bn-badge variant=${STATE_TONE[i.state] ?? "neutral"} dot>${i.state}</bn-badge>
        ${i.reconcile_paused
          ? html`<bn-badge variant="warning">reconcile paused</bn-badge>`
          : nothing}`,
      generations: `${i.generation ?? "—"} / ${i.observed_generation ?? "—"}`,
      node: i.node || "—",
      service: i.swarm_service_id || "—",
    }));

    return html`
      <div class="stack">
        <bn-card heading="Instances" flush>
          <div slot="actions" class="row">
            <bn-button size="sm" @click=${() => void this.loadInstances()}>Refresh</bn-button>
          </div>
          <div style="padding:var(--bino-space-lg) var(--bino-space-lg) 0">
            <div class="toolbar">
              <div class="search">
                <bn-field
                  label="Search instances"
                  hint="Matches the instance name, the slug, the hostname and the anchor Service UID."
                >
                  <bn-input
                    mono
                    .value=${this.instanceQuery}
                    placeholder="acme-production"
                    @input=${(e: CustomEvent<{ value: string }>) =>
                      (this.instanceQuery = e.detail.value)}
                  ></bn-input>
                </bn-field>
              </div>
              <bn-button
                variant="primary"
                @click=${() => {
                  this.resetPager("instances-table");
                  void this.loadInstances();
                }}
                >Search</bn-button
              >
            </div>
          </div>
          ${this.instancesError
            ? html`<div style="padding:0 var(--bino-space-lg) var(--bino-space-lg)">
                ${this.renderError("Instances unavailable", this.instancesError, () =>
                  void this.loadInstances(),
                )}
              </div>`
            : html`<bn-table
                id="instances-table"
                .columns=${INSTANCE_COLUMNS}
                .rows=${rows}
                clickable
                paginated
                .nextCursor=${this.instancesNext}
                ?loading=${this.instancesLoading}
                caption="Instances across every organization"
                @row-click=${(e: CustomEvent<BnRowClickDetail>) =>
                  void this.openInstanceDrawer(String(e.detail.id))}
                @page-change=${(e: CustomEvent<BnPageChangeDetail>) =>
                  void this.loadInstances(e.detail.cursor ?? undefined)}
              >
                <bn-empty-state
                  slot="empty"
                  icon="◲"
                  heading=${this.instanceQuery ? "No instance matches" : "No instances"}
                  message=${this.instanceQuery
                    ? "Nothing matched. The search covers the name, the slug, the hostname and the anchor Service UID."
                    : "No organization has provisioned an instance yet."}
                ></bn-empty-state>
              </bn-table>`}
        </bn-card>
        ${this.renderInstanceDrawer()}
      </div>
    `;
  }

  private renderInstanceDrawer(): TemplateResult {
    const d = this.openInstance;
    return html`
      <bn-drawer
        ?open=${this.drawerOpen}
        heading=${d ? d.instance.name : "Instance"}
        .width=${680}
        @close=${() => {
          this.drawerOpen = false;
          this.openInstance = null;
        }}
      >
        ${this.openInstanceLoading
          ? html`<bn-spinner block label="Reading instance"></bn-spinner>`
          : this.openInstanceError
            ? this.renderError("Instance unavailable", this.openInstanceError)
            : d
              ? this.renderInstanceDetail(d)
              : nothing}
        <bn-button
          slot="footer"
          @click=${() => {
            this.drawerOpen = false;
            this.openInstance = null;
          }}
          >Close</bn-button
        >
      </bn-drawer>
    `;
  }

  private renderInstanceDetail(d: FleetInstanceDetail): TemplateResult {
    const i = d.instance;
    const s = d.status;
    // The live generation's state is `active` and the restart boundary is decided
    // by `restartClass`, not by a string this page recognizes. Both were spelled
    // from memory here once ("live", "hot"), which is a badge that never goes green
    // and a change class that always reads restart — the exact drift `03` warns
    // about, and the reason both vocabularies are exported as one table.
    const genRows: BnRow[] = d.generations.map((g) => ({
      number: String(g.number),
      state: html`<bn-badge
        variant=${g.state === "active"
          ? "success"
          : g.state === "publish_failed"
            ? "error"
            : g.state === "publishing"
              ? "info"
              : "neutral"}
        >${GENERATION_STATE_LABELS[g.state] ?? g.state}</bn-badge
      >`,
      class: html`<bn-badge variant=${restartClass(g.change_class) ? "restart" : "reload"}
        >${CHANGE_CLASS_LABELS[g.change_class] ?? g.change_class}</bn-badge
      >`,
      digest: shortDigest(g.bundle_digest),
      by: g.created_by_label,
      when: ago(g.created),
    }));

    return html`
      <div class="stack">
        <div class="row">
          <bn-badge variant=${STATE_TONE[i.state] ?? "neutral"} dot>${i.state}</bn-badge>
          ${i.reconcile_paused
            ? html`<bn-badge variant="warning">reconcile paused</bn-badge>`
            : nothing}
          ${s?.reconcile.quarantined
            ? html`<bn-badge variant="error">quarantined</bn-badge>`
            : nothing}
        </div>

        <bn-kv
          .rows=${[
            { label: "Instance id", value: i.id },
            { label: "Organization", value: `${i.org_slug} (${i.org})` },
            { label: "Hostname", value: i.hostname },
            { label: "Size", value: i.size },
            { label: "Sluice version", value: i.sluice_version },
            { label: "Pinned image digest", value: i.image_digest },
            { label: "Anchor Service UID", value: i.swarm_service_id || undefined },
            { label: "Pinned node", value: i.node || undefined },
            { label: "Created", value: `${i.created} (${ago(i.created)})` },
          ] as BnKvRow[]}
          unsetText="not placed"
        ></bn-kv>

        <div>
          <h2>Desired versus observed</h2>
          ${d.status_error
            ? html`<bn-panel eyebrow=${d.status_error.code}>
                <p>
                  The cluster could not be observed for this read: ${d.status_error.message} The
                  record above is unaffected — the database is the source of truth, the cluster is a
                  projection of it.
                </p>
              </bn-panel>`
            : s
              ? html`<bn-kv
                  .rows=${[
                    { label: "Desired state", value: s.desired.state },
                    {
                      label: "Desired generation",
                      value: s.desired.generation === null ? undefined : String(s.desired.generation),
                    },
                    { label: "Observed state", value: s.observed.state },
                    {
                      label: "Observed generation",
                      value:
                        s.observed.generation === null ? undefined : String(s.observed.generation),
                    },
                    { label: "Live config digest", value: s.observed.config_digest ?? undefined },
                    {
                      label: "Replicas",
                      value: `${s.observed.replicas.running} / ${s.observed.replicas.desired}`,
                    },
                    {
                      label: "Update status",
                      value: s.observed.update_status
                        ? `${s.observed.update_status.state} — ${s.observed.update_status.message}`
                        : undefined,
                    },
                    { label: "Digest match", value: s.drift.digest_match ? "yes" : "no" },
                  ] as BnKvRow[]}
                  unsetText="none"
                ></bn-kv>`
              : nothing}
        </div>

        <div>
          <h2>Last reconcile</h2>
          <bn-kv
            .rows=${[
              {
                label: "Pass",
                value: d.reconcile.last_pass
                  ? `${d.reconcile.last_pass} (${ago(d.reconcile.last_pass)})`
                  : undefined,
              },
              { label: "Outcome", value: d.reconcile.outcome ?? undefined },
              { label: "Drift class", value: d.reconcile.drift_class ?? undefined },
              { label: "Message", value: d.reconcile.message || undefined },
              {
                label: "Consecutive failures",
                value: String(d.reconcile.consecutive_failures),
              },
              {
                label: "Reconcile paused",
                value: d.reconcile.paused
                  ? `yes — ${d.reconcile.paused_reason || "no reason recorded"}`
                  : "no",
              },
            ] as BnKvRow[]}
            unsetText="not recorded"
          ></bn-kv>
          ${d.reconcile.consecutive_failures >= 6
            ? html`<bn-panel eyebrow="Repair stopped">
                <p>
                  Six consecutive failed repairs stop automatic reconciliation for this instance. It
                  needs a person: a customer retry or an operator restarting the ladder.
                </p>
              </bn-panel>`
            : nothing}
        </div>

        <div>
          <h2>Recent publish history</h2>
          <bn-table
            .columns=${GENERATION_COLUMNS}
            .rows=${genRows}
            caption="Recent generations"
            empty="Nothing published yet."
          >
            <bn-empty-state
              slot="empty"
              icon="⌸"
              heading="Never published"
              message="This instance has no generation. It is a record with no deployment behind it."
            ></bn-empty-state>
          </bn-table>
          <p class="muted" style="margin-top:var(--bino-space-sm)">
            Publish history only. Staff do not see bundle content, rendered configuration or policy
            text — that is the customer's configuration, and reading it is not what this console is
            for.
          </p>
        </div>

        ${this.renderDiagnostics(i.id)}

        <p class="muted">
          The customer's own console for this instance is at
          <code>${hrefFor("instances", i.id)}</code>. Opening it reads with your staff principal and
          is recorded in that organization's activity feed — the customer sees that we looked.
        </p>
      </div>
    `;
  }



  /* ── support diagnostics bundle (`SC-F905`) ────────────────────────────── */

  /**
   * The diagnostics panel on an opened instance.
   *
   * **The exclusions are the feature, so they are on screen before the button is
   * pressed.** The bundle is built from an allow list: no customer row data (the
   * control plane never carries result rows on the query path, so there is no path
   * from the artifact to a row), no audit records and in particular no
   * `sql_sample`, no secret values — only the inventory — and no policy or rendered
   * configuration. The API returns its own manifest of both halves and this panel
   * renders it verbatim rather than paraphrasing, so the artifact and the screen
   * cannot drift into two accounts of what was collected.
   *
   * There is no confirmation dialog and no step-up ceremony, because the read stores
   * nothing and reveals strictly less than the panels above it plus the secret
   * inventory. What there IS, on every build and uncoalesced, is a row in the
   * customer's own activity feed — and the button says so before it is pressed rather
   * than afterwards.
   */
  private renderDiagnostics(instanceId: string): TemplateResult {
    const bundle = this.diagnostics?.instance.id === instanceId ? this.diagnostics : null;
    return html`<div>
      <h2>Support diagnostics bundle</h2>
      <p class="muted">
        One artifact with everything needed to debug this instance and nothing that would let us
        read the customer's data. Collecting it is recorded in
        <strong>${bundle?.instance.org_slug || "the organization"}'s own activity feed</strong>, on
        every build — the customer sees that we took a copy of their instance's state.
      </p>
      <div class="row" style="align-items:flex-end">
        <bn-field
          label="Ticket reference"
          hint="Optional here and required on the owner grant: a read that demanded a ticket number would be skipped during the incident it exists for."
        >
          <bn-input
            .value=${this.diagnosticsTicket}
            placeholder="SUP-1234"
            @input=${(e: CustomEvent<{ value: string }>) =>
              (this.diagnosticsTicket = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button
          size="sm"
          variant="primary"
          ?loading=${this.diagnosticsBuilding}
          @click=${() => void this.buildDiagnostics(instanceId)}
        >
          Collect bundle
        </bn-button>
        ${bundle
          ? html`<bn-button size="sm" @click=${() => this.downloadDiagnostics()}
              >Download JSON</bn-button
            >`
          : nothing}
      </div>
      ${this.diagnosticsError
        ? this.renderError("Diagnostics could not be collected", this.diagnosticsError)
        : nothing}
      ${bundle ? this.renderDiagnosticsResult(bundle) : nothing}
    </div>`;
  }

  /** The collected artifact, summarized. The download is the only copy that exists —
   * the bundle is assembled per request and stored nowhere, which is what keeps a
   * customer's operational state out of our database waiting to be a breach. */
  private renderDiagnosticsResult(bundle: DiagnosticsBundle): TemplateResult {
    return html`<div class="stack" style="margin-top:var(--bino-space-md)">
      <bn-kv
        .rows=${[
          { label: "Artifact", value: `${bundle.kind} v${bundle.version}` },
          { label: "Collected", value: `${bundle.collected_at} (${ago(bundle.collected_at)})` },
          { label: "Collected by", value: bundle.collected_by },
          { label: "Ticket", value: bundle.ticket || undefined },
          { label: "Control plane", value: bundle.control_plane },
          { label: "Generations", value: String(bundle.generations.length) },
          { label: "Secrets (inventory only)", value: String(bundle.secrets.length) },
          { label: "Cluster objects", value: String(bundle.objects.length) },
          { label: "Lifecycle events", value: String(bundle.events.length) },
          { label: "Jobs", value: String(bundle.jobs.length) },
        ] as BnKvRow[]}
        unsetText="none recorded"
      ></bn-kv>
      ${bundle.truncated?.length
        ? html`<bn-panel eyebrow="Some sections are capped">
            <ul class="bullets">
              ${bundle.truncated.map((t) => html`<li>${t}</li>`)}
            </ul>
          </bn-panel>`
        : nothing}
      <bn-panel eyebrow="What the bundle contains">
        <ul class="bullets">
          ${bundle.contains.map((c) => html`<li>${c}</li>`)}
        </ul>
      </bn-panel>
      <bn-panel eyebrow="What it deliberately omits">
        <ul class="bullets">
          ${bundle.omits.map((o) => html`<li>${o}</li>`)}
        </ul>
        <p class="muted">${bundle.audited}</p>
      </bn-panel>
    </div>`;
  }

  /* ── capacity and node inventory (`SC-F904`) ───────────────────────────── */

  /**
   * The capacity tab.
   *
   * **The disk column is the reason this tab is not a resource chart.** Swarm's
   * service spec has no disk field at all, so capacity is enforced outside the
   * substrate by a per-instance filesystem quota plus this ledger — and a state device
   * that fills does not slow anything down: the state volume holds the hash-chained
   * audit file, `audit.failClosed` defaults to true, and every instance pinned to that
   * node, from every organization, serves zero rows with a 503. So the alarm has its
   * own column, its own tone scale and its own copy, and it is never folded in beside
   * CPU.
   */
  private renderCapacity(): TemplateResult {
    if (this.capacityLoading && !this.capacity) {
      return html`<bn-card><bn-spinner block label="Reading the capacity ledger"></bn-spinner></bn-card>`;
    }
    if (this.capacityError) {
      return html`<div class="stack">
        ${this.renderError("Capacity ledger unavailable", this.capacityError, () =>
          void this.loadCapacity(),
        )}
      </div>`;
    }
    const view = this.capacity;
    if (!view) return html`<bn-card><p class="muted">Nothing read yet.</p></bn-card>`;

    return html`
      <div class="stack">
        <bn-panel eyebrow="What these numbers are">
          <p>
            Sums of <strong>reservations</strong> as the cluster reports them, not observed load.
            Nothing on this page holds anything: two concurrent creates can both be told a pool has
            room for one, and the loser is refused by the same admission check on its own attempt.
            Use it to plan, never to promise.
          </p>
          <ul class="bullets">
            ${view.caveats.map((c) => html`<li>${c}</li>`)}
          </ul>
        </bn-panel>

        ${view.link_available
          ? nothing
          : html`<bn-panel eyebrow="The ledger could not be read">
              <p>
                ${view.link_error?.message ??
                "This control plane has no capacity ledger wired."}
              </p>
              <p class="muted">
                This is <strong>not</strong> an empty fleet — it is an unreadable one. The size table
                and the isolation limits below are properties of the substrate and are true either
                way. While the ledger is unreadable, admission control is skipped: a create the
                cluster cannot place is caught by the reconciler minutes later instead of before any
                object exists.
              </p>
              ${view.link_error
                ? html`<bn-kv
                    .rows=${[{ label: "Error code", value: view.link_error.code }] as BnKvRow[]}
                  ></bn-kv>`
                : nothing}
            </bn-panel>`}

        ${view.link_available
          ? html`<bn-card heading="Fleet">
              <div slot="actions" class="row">
                <bn-badge variant="neutral">${view.control_plane}</bn-badge>
                <bn-button size="sm" @click=${() => void this.loadCapacity()}>Refresh</bn-button>
              </div>
              <div class="counters">
                ${counter(view.pools.length, "pools")} ${counter(view.nodes, "nodes")}
                ${counter(view.available_nodes, "available")}
              </div>
              <p class="muted">Read ${ago(view.observed_at)}.</p>
            </bn-card>`
          : nothing}

        ${view.pools.map((pool) => this.renderPool(pool, view))}

        <bn-card heading="Size classes">
          <bn-table
            .columns=${[
              { key: "size", label: "Size", mono: true, width: "1px" },
              { key: "memory", label: "Memory reserved", align: "right", width: "1px", mono: true },
              { key: "cpu", label: "CPU reserved", align: "right", width: "1px", mono: true },
              { key: "state", label: "State planning figure", align: "right", mono: true },
            ] as BnColumn[]}
            .rows=${view.sizes.map((sz) => ({
              id: sz.size,
              size: sz.size,
              memory: bytes(sz.reserved_memory_bytes),
              cpu: cores(sz.reserved_nano_cpus),
              state: bytes(sz.state_volume_bytes),
            })) as BnRow[]}
            caption="What one instance of each size commits"
            empty="No size table."
          ></bn-table>
          <p class="muted" style="margin-top:var(--bino-space-sm)">
            Memory is reserved at 100 % of the limit, which is why the counts above are
            deliberately unflattering: a 64 GiB worker holds 15 medium instances and not 16, however
            idle they are. Publishing the optimistic number is how a fleet ends up with tasks that
            never schedule. The state figure is a planning figure, no per-instance quota on this
            deployment: every instance's state shares one host directory, and query spill goes into
            it too.
          </p>
        </bn-card>

        <bn-card heading="What the substrate cannot isolate">
          <ul class="bullets">
            ${view.uncontrolled.map((u) => html`<li>${u}</li>`)}
          </ul>
          <p class="muted">
            Kubernetes on a shared node provides <strong>resource</strong> isolation, not
            <strong>security</strong> isolation. The answer for a customer who needs hard isolation is a dedicated node pool
            with different hardware, not a cleverer configuration.
          </p>
        </bn-card>
      </div>
    `;
  }

  /** One pool card: the aggregate, then its nodes. */
  private renderPool(pool: PoolRow, view: CapacityView): TemplateResult {
    const rows: BnRow[] = pool.members.map((n) => ({
      id: n.id,
      node: html`${n.id}
        ${n.available ? nothing : html`<span class="sub">unavailable — drained or unreachable</span>`}`,
      class: n.class || "—",
      memory: html`${bytes(n.free_memory_bytes)}
        <span class="sub">of ${bytes(n.advertisable_memory_bytes)}</span>`,
      cpu: cores(n.free_nano_cpus),
      state: n.state_device_bytes
        ? html`${bytes(n.free_state_bytes)}<span class="sub">of ${bytes(n.state_device_bytes)}</span>`
        : html`<span class="sub">not inventoried</span>`,
      alarm: html`<bn-badge
        .variant=${ALARM_TONE[n.state_alarm]}
        ?dot=${n.state_alarm === "critical"}
        >${ALARM_LABEL[n.state_alarm]}</bn-badge
      >`,
      fits: fitsText(n.fits, view),
    }));

    return html`<bn-card heading=${pool.dedicated ? `Pool ${pool.pool}` : "Shared pool"} flush>
      <div slot="actions" class="row">
        ${pool.dedicated
          ? html`<bn-badge variant="info"
              >dedicated to ${pool.org_slug || pool.org || "an unknown organization"}</bn-badge
            >`
          : html`<bn-badge variant="neutral">shared hardware</bn-badge>`}
        <bn-badge .variant=${ALARM_TONE[pool.state_alarm]}>${ALARM_LABEL[pool.state_alarm]}</bn-badge>
      </div>
      <div style="padding:var(--bino-space-lg) var(--bino-space-lg) 0">
        <bn-kv
          .rows=${[
            { label: "Nodes", value: `${pool.available_nodes} available of ${pool.nodes}` },
            {
              label: "Largest free memory block",
              value: bytes(pool.largest_free_memory_bytes),
            },
            { label: "Free CPU across the pool", value: cores(pool.free_nano_cpus) },
            {
              label: "Audit volume headroom",
              value: pool.state_device_bytes
                ? `${bytes(pool.free_state_bytes)} of ${bytes(pool.state_device_bytes)}`
                : undefined,
            },
            { label: "Still fits", value: fitsText(pool.fits, view) },
          ] as BnKvRow[]}
          unsetText="not inventoried"
        ></bn-kv>
        <p class="muted">
          The largest free <em>block</em> is what answers "what would fit" — a task runs on one node,
          so a pool total is a trend line and nothing more.
          ${pool.critical_nodes > 0
            ? html`<strong>
                ${pool.critical_nodes}
                ${pool.critical_nodes === 1 ? "node" : "nodes"} here can take every pinned instance's
                data plane down by filling its audit volume.</strong
              >`
            : nothing}
          ${pool.uninventoried_nodes > 0
            ? html` ${pool.uninventoried_nodes}
              ${pool.uninventoried_nodes === 1 ? "node reports" : "nodes report"} no device size, so
              their disk headroom is unknown rather than fine.`
            : nothing}
        </p>
      </div>
      <bn-table
        .columns=${NODE_COLUMNS}
        .rows=${rows}
        caption=${`Nodes in ${pool.pool}`}
        empty="No nodes in this pool."
      >
        <bn-empty-state
          slot="empty"
          icon="◌"
          heading="No nodes"
          message="This pool is labelled on no node, so nothing can be placed in it. That is an operator problem rather than a capacity one."
        ></bn-empty-state>
      </bn-table>
    </bn-card>`;
  }

  /* ── quarantine ────────────────────────────────────────────────────────── */

  private renderQuarantine(): TemplateResult {
    const rows: BnRow[] = this.orphans.map((o) => ({
      id: o.swarm_id,
      name: o.swarm_name,
      kind: o.object_kind,
      org: o.org_label || "—",
      instance: o.instance_label || "—",
      stage: o.quarantined_at
        ? o.acknowledged_at
          ? html`<bn-badge variant="error">removal acknowledged</bn-badge>`
          : html`<bn-badge variant="warning" dot>quarantined</bn-badge>`
        : html`<bn-badge variant="neutral">candidate</bn-badge>`,
      seen: String(o.observations),
      since: ago(o.first_seen),
      actions: o.quarantined_at
        ? html`<div class="row">
            <bn-button size="sm" @click=${() => this.openDialog({ kind: "quarantine-release", object: o })}
              >Release</bn-button
            >
            ${o.acknowledged_at
              ? nothing
              : html`<bn-button
                  size="sm"
                  variant="danger"
                  @click=${() => this.openDialog({ kind: "quarantine-ack", object: o })}
                  >Acknowledge removal</bn-button
                >`}
          </div>`
        : html`<span class="muted">no action taken</span>`,
    }));

    return html`
      <div class="stack">
        <bn-panel eyebrow="The ladder">
          <p>
            Passes one and two observe and record and do nothing. On pass
            ${this.reconcile?.orphan_observation_threshold ?? 3} the Service is stamped and its Pod is
            deleted. The Service, the Secrets and the data directory remain. Removal happens 72 hours
            after an operator acknowledges it and deletes the Service, the Secrets and the route. The
            data directory stays on the node until an operator removes it. If a quarantined
            instance's database record reappears, the next pass un-quarantines it and starts its Pod
            again, so its data directory and its audit chain survive.
          </p>
          <p class="muted">
            Nothing on this page deletes anything. Acknowledging removal schedules it and is
            recorded against you. Releasing records a request; the reconciler restores the Pod when
            the database record reappears.
          </p>
        </bn-panel>

        <bn-card heading="Orphaned objects" flush>
          <div slot="actions" class="row">
            <bn-checkbox
              label="Include candidates"
              .checked=${this.includeCandidates}
              @change=${(e: CustomEvent<{ checked: boolean }>) => {
                this.includeCandidates = e.detail.checked;
                this.resetPager("orphans-table");
                void this.loadOrphans();
              }}
            ></bn-checkbox>
            <bn-button size="sm" @click=${() => void this.loadOrphans()}>Refresh</bn-button>
          </div>
          ${this.orphansError
            ? html`<div style="padding:var(--bino-space-lg)">
                ${this.renderError("Quarantine list unavailable", this.orphansError, () =>
                  void this.loadOrphans(),
                )}
              </div>`
            : html`<bn-table
                id="orphans-table"
                .columns=${QUARANTINE_COLUMNS}
                .rows=${rows}
                ?loading=${this.orphansLoading}
                paginated
                .nextCursor=${this.orphansNext}
                caption="Candidate and quarantined orphans"
                @page-change=${(e: CustomEvent<BnPageChangeDetail>) =>
                  void this.loadOrphans(e.detail.cursor ?? undefined)}
              >
                <bn-empty-state
                  slot="empty"
                  icon="◌"
                  heading="Nothing orphaned"
                  message=${this.includeCandidates
                    ? "Every managed object has a matching desired-state record. This is the state the fleet should normally be in."
                    : "Nothing is quarantined. Turn on candidates to see objects the reconciler is still watching before it acts."}
                ></bn-empty-state>
              </bn-table>`}
        </bn-card>
      </div>
    `;
  }

  /* ── dialogs ───────────────────────────────────────────────────────────── */

  private renderDialog(): TemplateResult {
    const d = this.dialog;
    if (!d) return html``;
    const close = () => (this.dialog = null);
    const reasonField = (hint: string) => html`
      <bn-field label="Reason" hint=${hint} required>
        <bn-input
          .value=${this.reason}
          @input=${(e: CustomEvent<{ value: string }>) => (this.reason = e.detail.value)}
        ></bn-input>
      </bn-field>
    `;
    const errorRow = this.dialogError
      ? html`<bn-panel eyebrow=${this.dialogError.code}>
          <p role="alert">${this.dialogError.message}</p>
          ${this.dialogError.requestId
            ? html`<p class="muted">Reference <code>${this.dialogError.requestId}</code></p>`
            : nothing}
        </bn-panel>`
      : nothing;

    if (d.kind === "override") {
      const g = this.reconcile?.guard;
      return html`
        <bn-modal open tone="destructive" heading="Override the blast-radius guard?" .width=${640} @close=${close}>
          <p>
            This permits <strong>one</strong> pass to execute the plan the guard refused. The guard
            re-arms immediately afterwards. Your identity and this reason are recorded in the
            control-plane trail.
          </p>
          ${reasonField("Name the incident or ticket, and why the plan is correct.")} ${errorRow}
          <p slot="consequence">
            The next pass will take ${g?.planned_action_count ?? "the planned"} destructive actions:
            quarantined instances are stamped and their Pods deleted, and objects marked for removal
            are removed. If the plan is wrong because the control-plane database is missing rows — a
            restore from an older snapshot looks exactly like this — overriding stops running
            customer instances and removes their Services and Secrets. Read the plan before you
            confirm.
          </p>
          <bn-button slot="footer" @click=${close}>Cancel</bn-button>
          <bn-button
            slot="footer"
            variant="danger"
            ?disabled=${this.reason.trim() === ""}
            ?loading=${this.submitting}
            @click=${() =>
              void this.submit(
                (verification) => acknowledgeBlastRadius({ reason: this.reason }, { verification }),
                null,
                "The next pass is permitted to run once.",
              )}
            >Override this once</bn-button
          >
        </bn-modal>
      `;
    }

    if (d.kind === "mode") {
      const toObserve = d.to === "observe";
      return html`
        <bn-modal
          open
          tone=${toObserve ? "destructive" : "default"}
          heading=${toObserve ? "Stop the reconciler mutating anything?" : "Resume enforcing?"}
          .width=${560}
          @close=${close}
        >
          <p>
            ${toObserve
              ? "Fleet-wide observe-only. The reconciler keeps observing and reporting and performs no mutation against any instance in any organization."
              : "The reconciler resumes repairing drift across the fleet."}
          </p>
          ${reasonField(
            toObserve
              ? "What made you suspect the reconciler? This is recorded with its duration."
              : "What was resolved?",
          )}
          ${errorRow}
          <p slot="consequence">
            ${toObserve
              ? "Nothing self-heals while this is on: a phantom service is not recreated, a drifted spec is not corrected, and a failed publish is not retried. Instances that break during this window stay broken until it is turned off."
              : "The first pass after this may execute repairs that accumulated while the reconciler was observing. The blast-radius guard still applies and will refuse a pass that is too large."}
          </p>
          <bn-button slot="footer" @click=${close}>Cancel</bn-button>
          <bn-button
            slot="footer"
            variant=${toObserve ? "danger" : "primary"}
            ?disabled=${this.reason.trim() === ""}
            ?loading=${this.submitting}
            @click=${() =>
              void this.submit(
                (verification) => setReconcileMode(d.to, { reason: this.reason }, { verification }),
                null,
                toObserve ? "The reconciler is observe-only." : "The reconciler is enforcing again.",
              )}
            >${toObserve ? "Switch to observe-only" : "Resume enforcing"}</bn-button
          >
        </bn-modal>
      `;
    }

    if (d.kind === "grant") {
      const options: BnSelectOption[] = this.grantMembers.map((m) => ({
        value: m.account,
        label: m.email,
        description: `${m.name || "no display name"} · ${m.role}${
          m.last_seen ? ` · last seen ${ago(m.last_seen)}` : " · never signed in"
        }`,
        disabled: m.role === "owner",
        disabledReason: "Already an owner of this organization.",
      }));
      const ready = this.grantAccount !== "" && this.ticket.trim() !== "" && this.reason.trim() !== "";
      return html`
        <bn-modal
          open
          tone="destructive"
          heading=${`Grant owner in ${d.org.slug}?`}
          .width=${640}
          @close=${close}
        >
          <p>
            This is the only membership-mutating action in the console, and it exists for one
            situation: an organization whose sole owner has become unreachable and which the
            last-owner invariant would otherwise strand. It <strong>grants</strong> — there is no
            demote and no remove here, and the account must already be a member.
          </p>
          ${this.grantMembersError
            ? html`<bn-panel eyebrow="Members unavailable">
                <p>${this.grantMembersError.message}</p>
              </bn-panel>`
            : html`
                <bn-field
                  label="Member to promote"
                  hint="Only existing members appear. Staff cannot add anyone to an organization."
                  required
                >
                  <bn-select
                    filterable
                    .options=${options}
                    .value=${this.grantAccount}
                    .emptyText=${"This organization has no members to promote."}
                    placeholder="Choose a member"
                    @change=${(e: CustomEvent<BnSelectChangeDetail>) =>
                      (this.grantAccount = e.detail.value)}
                  ></bn-select>
                </bn-field>
              `}
          <bn-field label="Ticket reference" hint="Recorded on the action. There is no path without one." required>
            <bn-input
              mono
              .value=${this.ticket}
              placeholder="SUP-1234"
              @input=${(e: CustomEvent<{ value: string }>) => (this.ticket = e.detail.value)}
            ></bn-input>
          </bn-field>
          ${reasonField("What the customer asked for, in one line.")} ${errorRow}
          <p slot="consequence">
            The moment this succeeds, a control-plane audit event appears in
            <strong>${d.org.slug}</strong>'s own activity feed — visible to their members, not only
            to us — and every existing member of the organization is emailed, including the owner
            who could not be reached. This action needs the break-glass staff role and an identity
            check bound to this organization.
          </p>
          <bn-button slot="footer" @click=${close}>Cancel</bn-button>
          <bn-button
            slot="footer"
            variant="danger"
            ?disabled=${!ready}
            ?loading=${this.submitting}
            @click=${() =>
              void this.submit(
                (verification) =>
                  grantOwner(
                    d.org.id,
                    { account: this.grantAccount, ticket: this.ticket, reason: this.reason },
                    { verification },
                  ),
                { action: "change_owner", target: d.org.id },
                "Owner granted. The organization's members have been notified.",
              )}
            >Grant owner</bn-button
          >
        </bn-modal>
      `;
    }

    const obj = d.object;
    const removal = d.kind === "quarantine-ack";
    return html`
      <bn-modal
        open
        tone="destructive"
        heading=${removal ? `Acknowledge removal of ${obj.swarm_name}?` : `Release ${obj.swarm_name}?`}
        .width=${600}
        @close=${close}
      >
        <bn-kv
          .rows=${[
            { label: "Object UID", value: obj.swarm_id },
            { label: "Kind", value: obj.object_kind },
            { label: "Org label", value: obj.org_label || undefined },
            { label: "Instance label", value: obj.instance_label || undefined },
            { label: "First seen", value: `${obj.first_seen} (${ago(obj.first_seen)})` },
            { label: "Quarantine reason", value: obj.quarantine_reason || undefined },
          ] as BnKvRow[]}
          unsetText="no label"
        ></bn-kv>
        <p style="margin-top:var(--bino-space-md)">
          ${removal
            ? "Acknowledging schedules removal 72 hours from now. Nothing is removed by pressing this."
            : "Releasing records a request to return this object to service. It changes nothing on the cluster: the reconciler clears the quarantine and starts the Pod again once the database record reappears."}
        </p>
        ${reasonField("Recorded with your identity on the object.")} ${errorRow}
        <p slot="consequence">
          ${removal
            ? "After the 72 hours the Service, the Secrets and the route are deleted. The data directory stays on the node until an operator removes it. Removal cannot be undone, which is why quarantine exists in front of it."
            : "Once restored, the Pod serves again on its existing data directory. If it is genuinely an orphan, with no desired-state record anywhere, it stays quarantined and nothing serves."}
        </p>
        <bn-button slot="footer" @click=${close}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="danger"
          ?disabled=${this.reason.trim() === ""}
          ?loading=${this.submitting}
          @click=${() =>
            void this.submit(
              (verification) =>
                removal
                  ? acknowledgeQuarantineRemoval(obj.swarm_id, { reason: this.reason }, { verification })
                  : releaseQuarantine(obj.swarm_id, { reason: this.reason }, { verification }),
              null,
              removal ? "Removal acknowledged." : "Release recorded.",
            )}
          >${removal ? "Acknowledge removal" : "Release now"}</bn-button
        >
      </bn-modal>
    `;
  }

  /* ── render ────────────────────────────────────────────────────────────── */

  override render() {
    return html`
      <div class="console">
        <div class="band" role="note">
          Staff console · every organization · every action recorded
        </div>
        <div class="frame">
          <header class="page">
            <h1>Backoffice</h1>
          </header>
          <bn-panel eyebrow="Boundary">
            <p>
              This is Sluice Cloud's own operator console. It reads across every organization, and
              it is not a customer surface. Staff see <strong>state and shape</strong> —
              organizations, instances, generations, reconcile outcomes — and never a secret value,
              never a customer's policy content and never their query results. Every action here is
              recorded against your staff identity, and its organization-visible half appears in
              that organization's own activity feed: the customer sees that we looked.
            </p>
          </bn-panel>

          <div style="margin-top:var(--bino-space-lg)">
            <bn-tabs
              label="Backoffice sections"
              .tabs=${TABS}
              .active=${this.tab}
              @change=${(e: CustomEvent<BnTabsChangeDetail>) => this.selectTab(e.detail.id)}
            >
              <div slot="fleet">${this.tab === "fleet" ? this.renderFleet() : nothing}</div>
              <div slot="orgs">${this.tab === "orgs" ? this.renderOrgs() : nothing}</div>
              <div slot="instances">
                ${this.tab === "instances" ? this.renderInstances() : nothing}
              </div>
              <div slot="quarantine">
                ${this.tab === "quarantine" ? this.renderQuarantine() : nothing}
              </div>
              <div slot="capacity">${this.tab === "capacity" ? this.renderCapacity() : nothing}</div>
            </bn-tabs>
          </div>
        </div>
      </div>
      ${this.renderDialog()}
    `;
  }
}

/** bytes renders a byte count in binary units, coarsely: the number's job is to be
 * recognizable as a size class rather than to be arithmetic. A NEGATIVE value is
 * printed with its sign rather than clamped, because an over-committed node is the
 * most urgent row on the page and clamping would render it as merely full. */
function bytes(n: number): string {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const gib = 1024 ** 3;
  const mib = 1024 ** 2;
  if (abs >= gib) return `${sign}${(abs / gib).toFixed(1)} GiB`;
  if (abs >= mib) return `${sign}${(abs / mib).toFixed(1)} MiB`;
  return `${sign}${abs} B`;
}

/** cores renders nano-CPUs as whole cores to one decimal place. */
function cores(nano: number): string {
  return `${(nano / 1e9).toFixed(1)} cores`;
}

/** fitsText renders the per-size counts in the size table's own order, so two pools
 * read left to right identically. A pool that fits nothing renders "none" rather than
 * four zeros. */
function fitsText(fits: Record<string, number>, view: CapacityView): string {
  const parts = view.sizes
    .map((sz) => ({ size: sz.size, n: fits[sz.size] ?? 0 }))
    .filter((p) => p.n > 0)
    .map((p) => `${p.n} × ${p.size}`);
  return parts.length ? parts.join(", ") : "none";
}

/** One headline number. Local because it is three lines and used once. */
function counter(n: number, label: string): TemplateResult {
  return html`<div class="counter"><span class="n">${n}</span><span class="k">${label}</span></div>`;
}

declare global {
  interface HTMLElementTagNameMap {
    "page-backoffice": PageBackoffice;
  }
}
